/**
 * Admin Launches Page
 *
 * Displays list of all launches with status filters and management options.
 * Allows admins to review, approve, and reject launches.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getAdminLaunches,
  type AdminLaunch,
  type AdminLaunchesListResponse,
  type LaunchStatus,
} from '../services/adminApi.js';
import { AdminLayout } from '../components/AdminLayout.js';
import { ErrorDisplay } from '../components/admin/ErrorDisplay.js';
import { LaunchStatusBadge } from '../components/admin/LaunchStatusBadge.js';

/**
 * Format XRP from drops
 */
function formatXrp(drops: number | null | undefined): string {
  if (drops === null || drops === undefined) return '-';
  return `${(Number(drops) / 1_000_000).toFixed(2)} XRP`;
}

/**
 * Format date to relative time or date string
 */
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * Status filter tabs
 */
const STATUS_TABS: { key: LaunchStatus | 'all'; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'pending_approval', label: 'Pending Approval' },
  { key: 'pending_authorization', label: 'Pending Auth' },
  { key: 'active', label: 'Active' },
  { key: 'completed', label: 'Completed' },
  { key: 'draft', label: 'Drafts' },
];

export function AdminLaunchesPage() {
  const navigate = useNavigate();
  const [data, setData] = useState<AdminLaunchesListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<LaunchStatus | 'all'>('all');
  const [page, setPage] = useState(1);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await getAdminLaunches({
        page,
        limit: 50,
        status: statusFilter,
      });
      setData(result);
    } catch (err: any) {
      setError(err.message || 'Failed to load launches');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [statusFilter, page]);

  const handleStatusChange = (newStatus: LaunchStatus | 'all') => {
    setStatusFilter(newStatus);
    setPage(1); // Reset to first page when changing filter
  };

  const launches = data?.launches || [];
  const statusCounts = data?.statusCounts || {};
  const pagination = data?.pagination;

  // Calculate total pending for the badge
  const pendingApprovalCount = statusCounts['pending_approval'] || 0;

  return (
    <AdminLayout>
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Launches</h1>
            <p className="mt-2 text-sm text-gray-600">
              Review and manage NFT collection launches
            </p>
          </div>
          <button
            onClick={loadData}
            disabled={loading}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        {/* Stats Summary */}
        <div className="mb-6 grid gap-4 sm:grid-cols-4">
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <div className="text-sm font-medium text-gray-500">Total Launches</div>
            <div className="mt-1 text-2xl font-bold text-gray-900">
              {Object.values(statusCounts).reduce((a, b) => a + b, 0)}
            </div>
          </div>
          <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 shadow-sm">
            <div className="text-sm font-medium text-yellow-600">Pending Approval</div>
            <div className="mt-1 text-2xl font-bold text-yellow-700">
              {pendingApprovalCount}
            </div>
          </div>
          <div className="rounded-lg border border-green-200 bg-green-50 p-4 shadow-sm">
            <div className="text-sm font-medium text-green-600">Active</div>
            <div className="mt-1 text-2xl font-bold text-green-700">
              {statusCounts['active'] || 0}
            </div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <div className="text-sm font-medium text-gray-500">Completed</div>
            <div className="mt-1 text-2xl font-bold text-gray-900">
              {statusCounts['completed'] || 0}
            </div>
          </div>
        </div>

        {/* Status Filter Tabs */}
        <div className="mb-6 flex flex-wrap gap-2 border-b border-gray-200 pb-4">
          {STATUS_TABS.map((tab) => {
            const count = tab.key === 'all'
              ? Object.values(statusCounts).reduce((a, b) => a + b, 0)
              : statusCounts[tab.key] || 0;
            const isActive = statusFilter === tab.key;

            return (
              <button
                key={tab.key}
                onClick={() => handleStatusChange(tab.key)}
                className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {tab.label}
                <span
                  className={`rounded-full px-2 py-0.5 text-xs ${
                    isActive ? 'bg-blue-200' : 'bg-gray-200'
                  }`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Error State */}
        {error && (
          <ErrorDisplay error={error} onRetry={loadData} className="mb-6" />
        )}

        {/* Loading State */}
        {loading && launches.length === 0 && (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
              <p className="mt-4 text-sm text-gray-600">Loading launches...</p>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && launches.length === 0 && (
          <div className="rounded-lg border border-gray-200 bg-white p-12 text-center">
            <p className="text-gray-500">
              {statusFilter === 'all'
                ? 'No launches found.'
                : `No launches with status "${statusFilter.replace('_', ' ')}".`}
            </p>
          </div>
        )}

        {/* Launches Table */}
        {launches.length > 0 && (
          <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Collection
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Creator
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Items
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Price
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Fee Paid
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Created
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {launches.map((launch) => (
                  <tr
                    key={launch.id}
                    onClick={() => navigate(`/admin/launches/${launch.id}`)}
                    className="hover:bg-gray-50 cursor-pointer"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {launch.thumbnail_url && (
                          <img
                            src={launch.thumbnail_url}
                            alt=""
                            className="mr-3 h-10 w-10 rounded-full object-cover"
                          />
                        )}
                        <div>
                          <div className="text-sm font-medium text-gray-900">{launch.name}</div>
                          <div className="text-xs text-gray-500">/{launch.slug}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {launch.creator_username || 'Unknown'}
                      </div>
                      <div className="text-xs text-gray-500 font-mono">
                        {launch.creator_address.slice(0, 8)}...{launch.creator_address.slice(-4)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <LaunchStatusBadge status={launch.status} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {launch.total_items.toLocaleString()}
                      {launch.minted_count > 0 && (
                        <span className="ml-1 text-green-600">
                          ({launch.minted_count} minted)
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatXrp(launch.mint_price_drops)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {launch.fee_paid ? (
                        <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-800">
                          Yes
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600">
                          No
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(launch.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <div className="flex items-center justify-between border-t border-gray-200 bg-gray-50 px-6 py-3">
                <div className="text-sm text-gray-700">
                  Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                  {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                  {pagination.total} launches
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={pagination.page <= 1}
                    className="rounded-md border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-gray-700">
                    Page {pagination.page} of {pagination.totalPages}
                  </span>
                  <button
                    onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                    disabled={pagination.page >= pagination.totalPages}
                    className="rounded-md border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
